<template>
  <div class="TravelDestinationTypeForm">
    <h2 class="gl_form-title">
      {{ t(`copy_travel_destination-type_form-title-${forWhom}`, answers.forWhomPeople) }}
    </h2>

    <CardInfoComponent
      v-if="isSeveralTravelsType"
      :icon="configurations.tags.allYearAlert?.icon"
    >
      {{ configurations.tags.allYearAlert?.label }}
    </CardInfoComponent>

    <RadioButtonGroup
      id="type"
      :value="destinationType"
      :options="destinationTypesOptions"
      v-model="destinationType"
      :rules="[validationRules.required]"
    />
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import type TravelConfigurations from '@/modules/travel/common/models/APP/TravelConfigurations'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import type TravelRaterAnswers from '../../models/APP/TravelRaterAnswers'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'

  const { t } = useI18n()
  const { required } = useRules()

  defineExpose({ triggerSubmit })
  interface PropsInterface {
    nextForm: String[]
    answers: TravelRaterAnswers
    configurations: TravelConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const forWhom = computed(() => props.answers.forWhom ?? 'me')
  const hasSportInsurance = computed(
    () =>
      insuranceTypes.value.studyAbroad.id === String(props.answers.insuranceType) &&
      props.answers.sportInsurance
  )
  const isSeveralTravelsType = computed(
    () => insuranceTypes.value.several.id === String(props.answers.insuranceType)
  )
  const destinationTypesOptions = computed(() => {
    const options = props.configurations.destinationTypesOptions.map((dto) => {
      if (
        [
          destinationTypes.value.europeAndMediterraneanCoastCountries.id,
          destinationTypes.value.spainEuropeAndMediterraneanCoastCountries.id
        ].includes(dto.value)
      ) {
        dto.extra = { alert: props.configurations.tags.europeAndMediterraneanCountriesAlert }
      }
      return dto
    })
    if (hasSportInsurance.value) {
      return options.filter((dto) =>
        [
          destinationTypes.value.europeAndMediterraneanCoastCountries.id,
          destinationTypes.value.restOfTheWorld.id
        ].includes(dto.value)
      )
    }
    return options.filter(
      (dto) =>
        ![destinationTypes.value.spainEuropeAndMediterraneanCoastCountries.id].includes(dto.value)
    )
  })
  const destinationTypes = computed(() => props.configurations.destinationTypes)
  const insuranceTypes = computed(() => props.configurations.insuranceTypes)
  const destinationType = ref(
    props.answers.destinationType
      ? String(props.answers.destinationType)
      : destinationTypesOptions.value[0].value
  )

  const validationRules = {
    required
  }

  function getNextStep() {
    if (insuranceTypes.value.several.id === String(props.answers.insuranceType)) {
      return undefined // finish
    }

    if (
      insuranceTypes.value.cruise.id === String(props.answers.insuranceType) ||
      hasSportInsurance.value
    ) {
      return props.nextForm[0] // 'TravelDuration'
    }

    return props.nextForm[1] // 'TravelCancellationCoverage'
  }

  function triggerSubmit() {
    const page = getNextStep()
    const answers = {
      destinationType: Number(destinationType.value)
    }

    return { page, answers }
  }
</script>

<style lang="scss" scoped>
  .gl_form-title {
    margin-bottom: $spacing-xl;
  }
</style>
